import { useState, useEffect } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../components/ui_components/CommonComponentsStructureCss';
import { apiGetLeastOwnedCards, apiGetCard } from '../../api/cardApi';
import type { LeastOwnedCard, Card, CardRarity } from '../../types/models/card';
import CardDetailModal from './CardDetailModal';
import { useAppSelector } from '../../store/hooks';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';
const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

const RARITY_LABEL: Record<CardRarity, string> = {
  common: 'Comune',
  rare: 'Rara',
  epic: 'Epica',
  legendary: 'Leggendaria',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 40px 24px 80px;
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 36px;
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(243, 63, 94, 0.7);
  margin-bottom: 8px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 900;
  color: #f43f5e;
  margin: 0 0 8px;
`;

const PageSub = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const CardWrapper = styled.div`
  background-color: #111;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
  position: relative;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(244, 63, 94, 0.35);
    box-shadow: 0 6px 20px rgba(244, 63, 94, 0.12);
  }
`;

const RarityBar = styled.div<{ $color: string }>`
  height: 3px;
  background: ${({ $color }) => $color};
  width: 100%;
`;

const CardImgArea = styled.div`
  aspect-ratio: 2 / 3;
  background: #0d0d0d;
  overflow: hidden;
  position: relative;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.1);
`;

const OwnersCountBadge = styled.div<{ $zero: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${({ $zero }) => ($zero ? 'rgba(244,63,94,0.85)' : 'rgba(0,0,0,0.7)')};
  border: 1px solid ${({ $zero }) => ($zero ? 'transparent' : 'rgba(255,255,255,0.15)')};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 20px;
  backdrop-filter: blur(4px);
`;

const CardInfo = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RarityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RarityDot = styled.div<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const RarityText = styled.span<{ $color: string }>`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const OwnersList = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.4;
  min-height: 14px;
`;

const WrestlerNames = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingText = styled.p`
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 60px 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const LeastOwnedCardsPage: React.FC = () => {
  const userId = useAppSelector((s) => s.auth.userLoggedState?.id ?? s.auth.user?.id ?? null);
  const isAuthenticated = useAppSelector(
    (s) => s.auth.isAuthenticated || s.auth.userLoggedState !== null
  );

  const [cards, setCards] = useState<LeastOwnedCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailCard, setDetailCard] = useState<Card | null>(null);
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);
  const [cardCache, setCardCache] = useState<Record<number, Card>>({});

  useEffect(() => {
    apiGetLeastOwnedCards()
      .then((res) => setCards(res))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = async (card: LeastOwnedCard) => {
    if (loadingDetailId === card.id) return;
    if (cardCache[card.id]) { setDetailCard(cardCache[card.id]); return; }
    setLoadingDetailId(card.id);
    try {
      const full = await apiGetCard(card.id);
      setCardCache((prev) => ({ ...prev, [card.id]: full }));
      setDetailCard(full);
    } catch { /* noop */ } finally {
      setLoadingDetailId(null);
    }
  };

  const getOwnedQty = (card: LeastOwnedCard) => {
    if (!userId) return 0;
    return card.owners.some((o) => o.id === userId) ? 1 : 0;
  };

  const formatOwners = (card: LeastOwnedCard) => {
    if (card.owners_count === 0) return 'Nessun possessore';
    const names = card.owners
      .slice(0, 2)
      .map((o) => o.nickname_forum ?? o.username)
      .join(', ');
    const extra = card.owners_count > 2 ? ` +${card.owners_count - 2}` : '';
    return names + extra;
  };

  return (
    <CommonWrapper>
      <PageInner>
        <PageHeader>
          <Eyebrow>Statistiche community</Eyebrow>
          <PageTitle>Introvabili</PageTitle>
          <PageSub>Le 25 carte meno possedute dalla community, ordinate dalla più esclusiva.</PageSub>
        </PageHeader>

        {loading && <LoadingText>Caricamento...</LoadingText>}

        {!loading && (
          <Grid>
            {cards.map((card) => {
              const src = toImgSrc(card.fronte_path);
              const rColor = RARITY_COLOR[card.rarity];
              const wrestlerLabel = card.wrestlers
                .slice(0, 2)
                .map((w) => w.name)
                .join(', ');
              return (
                <CardWrapper
                  key={card.id}
                  onClick={() => openDetail(card)}
                  style={{ cursor: loadingDetailId === card.id ? 'wait' : 'pointer' }}
                >
                  <RarityBar $color={rColor} />
                  <CardImgArea>
                    <OwnersCountBadge $zero={card.owners_count === 0}>
                      {card.owners_count === 0
                        ? '0 poss.'
                        : `${card.owners_count} poss.`}
                    </OwnersCountBadge>
                    {src
                      ? <CardImg src={src} alt={`Carta #${card.id}`} />
                      : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                    }
                  </CardImgArea>
                  <CardInfo>
                    <RarityRow>
                      <RarityDot $color={rColor} />
                      <RarityText $color={rColor}>{RARITY_LABEL[card.rarity]}</RarityText>
                    </RarityRow>
                    {wrestlerLabel && <WrestlerNames>{wrestlerLabel}</WrestlerNames>}
                    <OwnersList>{formatOwners(card)}</OwnersList>
                  </CardInfo>
                </CardWrapper>
              );
            })}
          </Grid>
        )}
      </PageInner>

      {detailCard && (
        <CardDetailModal
          card={detailCard}
          ownedQuantity={getOwnedQty(cards.find((c) => c.id === detailCard.id)!)}
          isAuthenticated={isAuthenticated}
          onClose={() => setDetailCard(null)}
        />
      )}
    </CommonWrapper>
  );
};

export default LeastOwnedCardsPage;
